/**
 * TanStack Query client configuration
 */

import { QueryClient, QueryClientConfig } from '@tanstack/react-query';

/**
 * Default query client configuration
 */
const queryClientConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      // Data caching strategy
      staleTime: 5 * 60 * 1000, // 5 minutes - data is considered fresh
      gcTime: 10 * 60 * 1000, // 10 minutes - cache garbage collection time (formerly cacheTime)

      // Retry configuration
      retry: 3, // Retry up to 3 times
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff

      // Refetch configuration
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: true,
    },
    mutations: {
      // Retry configuration for mutations
      retry: false, // Don't retry mutations by default (they may have side effects)

      // Error handling
      onError: (error) => {
        console.error('Mutation error:', error);
        // You can add global error handling here (e.g., toast notifications)
      },
    },
  },
};

/**
 * Create query client instance
 */
export const queryClient = new QueryClient(queryClientConfig);

/**
 * Query keys for consistent cache management
 */
export const queryKeys = {
  // Auth
  auth: {
    me: ['auth', 'me'] as const,
  },

  // Users
  users: {
    all: ['users'] as const,
    detail: (userId: string) => ['users', userId] as const,
    profile: (userId: string) => ['users', userId, 'profile'] as const,
  },

  // Photos
  photos: {
    all: ['photos'] as const,
    feed: (cursor?: string) => ['photos', 'feed', cursor] as const,
    detail: (photoId: string) => ['photos', photoId] as const,
    byUser: (userId: string, cursor?: string) => ['photos', 'user', userId, cursor] as const,
  },

  // Comments
  comments: {
    byPhoto: (photoId: string, cursor?: string) => ['comments', 'photo', photoId, cursor] as const,
  },

  // Likes
  likes: {
    byPhoto: (photoId: string, cursor?: string) => ['likes', 'photo', photoId, cursor] as const,
  },

  // Notifications
  notifications: {
    all: (cursor?: string) => ['notifications', cursor] as const,
    unreadCount: ['notifications', 'unread-count'] as const,
  },
};

export default queryClient;
